import { useCallback, useEffect, useMemo, useState } from 'react'

import type { DateRange } from 'react-day-picker'

import CalendarIcon from '@/assets/icons/icon-calendar.svg'
import { useDateRangeContext } from '@/components/provider/date-range-provider.tsx'
import { classNames } from '@/utils/helpers'

import { Button } from '../button.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '../popover.tsx'
import { Calendar } from './calendar.tsx'
import { DATE_RANGE_PRESETS, Preset, PresetLabel } from './constants.ts'
import {
  areDateRangesIntersecting,
  clampDate,
  formatDateRange,
  getSelectedPreset,
} from './utils.ts'

interface RangeDatePickerProps {
  date: DateRange
  /** Function to set the selected date range. */
  setDate: (range: DateRange) => void
  /** Additional class name for styling purposes. */
  className?: string
  maxDateRange?: DateRange
  dateNameSuffix?: string
}

export const CalendarRangeDatePicker = ({
  dateNameSuffix,
  className,
  date,
  setDate,
  maxDateRange,
}: RangeDatePickerProps) => {
  const {
    maxDateRange: globalMaxDataRange,
    today,
    timezoneLabel,
  } = useDateRangeContext()
  const dateSuffix = dateNameSuffix ?? timezoneLabel
  const maxRange = maxDateRange ?? globalMaxDataRange

  const presets = useMemo(() => {
    const presetItems = maxRange
      ? DATE_RANGE_PRESETS.filter((preset) =>
          areDateRangesIntersecting(preset.provider(today), maxRange),
        )
      : DATE_RANGE_PRESETS

    return [
      ...presetItems,
      {
        label: PresetLabel.ALL_TIME,
        isEnabled: !!maxRange?.from,
        provider: (todayDate) => {
          return {
            from: maxRange?.from ?? todayDate,
            to: maxRange?.to ?? todayDate,
          }
        },
      },
    ]
  }, [today, maxRange])

  const dateRangeByPreset = useCallback(
    ({ provider }: Preset) => {
      const range = provider(today)

      if (!range.from || !range.to || !maxRange?.from || !maxRange?.to) {
        return range
      }

      return {
        from: clampDate(range.from, maxRange),
        to: clampDate(range.to, maxRange),
      }
    },
    [today, maxRange],
  )

  const [currentDate, setCurrentDate] = useState<DateRange | undefined>(date)
  const selectedPreset = useMemo(
    () => getSelectedPreset(presets, date, dateRangeByPreset),
    [presets, date, dateRangeByPreset],
  )
  const selectedPresetInPicker = useMemo(
    () => getSelectedPreset(presets, currentDate, dateRangeByPreset),
    [presets, currentDate, dateRangeByPreset],
  )
  const setDateRangeToPreset = useCallback(
    (preset: Preset) => {
      const newDateRange: DateRange = dateRangeByPreset(preset)

      setDate(newDateRange)
      setCurrentDate(newDateRange)
    },
    [dateRangeByPreset, setDate],
  )

  useEffect(() => {
    if (!maxRange?.from || !maxRange?.to || !currentDate) return

    const newFrom = currentDate.from
      ? clampDate(currentDate.from, maxRange)
      : undefined
    const newTo = currentDate.to
      ? clampDate(currentDate.to, maxRange)
      : undefined

    setCurrentDate({ from: newFrom, to: newTo })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxRange])

  return (
    <div className={classNames('block gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="ghost"
            className={classNames(
              'bg-card text-title flex w-full items-center justify-start gap-1 border text-left font-normal',
              !currentDate && 'text-secondary-50',
            )}
            testId="date-picker-button"
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="truncate">
              {!selectedPresetInPicker ? (
                currentDate?.from && currentDate.to ? (
                  formatDateRange(currentDate, today, true)
                ) : (
                  <span>Pick a date</span>
                )
              ) : (
                selectedPresetInPicker.label
              )}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="z-[70] flex w-auto gap-8 p-4"
          align="end"
          side="bottom"
          avoidCollisions
        >
          <div className="hidden w-40 flex-col gap-2 md:flex">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                className={classNames('h-8', {
                  'border-primary-600 dark:border-primary-600':
                    selectedPreset && selectedPreset.label === preset.label,
                })}
                onClick={() => {
                  setDateRangeToPreset(preset)
                }}
                disabled={preset.isEnabled === false}
                variant="outline"
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <div>
            <div className="mb-4 flex flex-col gap-0">
              {(!currentDate?.from || !currentDate.to) && (
                <span className="text-danger-default text-sm font-medium">
                  Please select a date range.
                </span>
              )}
              {currentDate?.from && currentDate.to && (
                <span className="text-secondary-300 text-sm font-medium">
                  Select date range
                </span>
              )}
              <div>
                {currentDate?.from && currentDate.to ? (
                  <div className="text-title text-xl font-semibold max-w-[252px]">
                    {formatDateRange(currentDate, today)}{' '}
                    <span className="ml-1 text-xs text-gray-400">
                      {dateSuffix}
                    </span>
                  </div>
                ) : (
                  <span className="text-title text-xl font-semibold">
                    No date selected
                  </span>
                )}
              </div>
            </div>

            <Calendar
              initialFocus
              mode="range"
              defaultMonth={currentDate?.to}
              selected={currentDate}
              weekStartsOn={1}
              onSelect={(range) => {
                setCurrentDate(range)

                if (range && range.from && range.to) {
                  setDate(range)
                }
              }}
              numberOfMonths={1}
              fromDate={maxDateRange?.from ?? globalMaxDataRange.from}
              toDate={maxDateRange?.to ?? globalMaxDataRange.to}
              today={today}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
